export class AppError extends Error {
  constructor(
    message: string,
    public code: string,
    public severity: 'error' | 'warning' | 'info' = 'error',
    public metadata?: Record<string, any>
  ) {
    super(message);
    this.name = 'AppError';
  }
}

export enum ErrorCodes {
  AUTH_INVALID_CREDENTIALS = 'auth/invalid-credentials',
  AUTH_USER_NOT_FOUND = 'auth/user-not-found',
  AUTH_EMAIL_IN_USE = 'auth/email-in-use',
  AUTH_INVALID_EMAIL = 'auth/invalid-email',
  AUTH_EMAIL_NOT_VERIFIED = 'auth/email-not-verified',
  AUTH_EMAIL_RESEND_FAILED = 'auth/email-resend-failed',
  AUTH_UNKNOWN_ERROR = 'auth/unknown-error',
  NETWORK_OFFLINE = 'network/offline',
  NETWORK_TIMEOUT = 'network/timeout',
  NETWORK_SERVER_ERROR = 'network/server-error',
  SYNC_FAILED = 'sync/failed',
  SYNC_CONFLICT = 'sync/conflict',
  SYNC_TIMEOUT = 'sync/timeout',
}

export class AuthError extends AppError {
  constructor(message: string, code: ErrorCodes) {
    super(message, code, 'error');
    this.name = 'AuthError';
  }
}

export class NetworkError extends AppError {
  constructor(message: string, code: string, metadata?: Record<string, any>) {
    super(message, code, 'error', metadata);
    this.name = 'NetworkError';
  }
}

export class SyncError extends AppError {
  constructor(message: string, code: string, metadata?: Record<string, any>) {
    super(message, code, 'warning', metadata);
    this.name = 'SyncError';
  }
}

// User-friendly error messages
export function getErrorMessage(code: string): string {
  switch (code) {
    case ErrorCodes.AUTH_INVALID_CREDENTIALS:
      return 'Invalid email or password.';
    case ErrorCodes.AUTH_USER_NOT_FOUND:
      return 'No account found with this email.';
    case ErrorCodes.AUTH_EMAIL_IN_USE:
      return 'An account with this email already exists. Please sign in instead.';
    case ErrorCodes.AUTH_INVALID_EMAIL:
      return 'Please enter a valid email address.';
    case ErrorCodes.AUTH_EMAIL_NOT_VERIFIED:
      return 'Please check your email to confirm your account before signing in.';
    case ErrorCodes.AUTH_EMAIL_RESEND_FAILED:
      return 'Failed to resend confirmation email. Please try again.';
    case ErrorCodes.NETWORK_OFFLINE:
      return 'You appear to be offline';
    case ErrorCodes.NETWORK_TIMEOUT:
      return 'Request timed out. Please try again';
    case ErrorCodes.NETWORK_SERVER_ERROR:
      return 'Server error. Please try again later';
    case ErrorCodes.SYNC_FAILED:
      return 'Failed to sync your changes';
    case ErrorCodes.SYNC_CONFLICT:
      return 'Conflict detected. Please refresh and try again';
    case ErrorCodes.SYNC_TIMEOUT:
      return 'Sync timed out. Changes will retry automatically';
    default:
      return 'An unexpected error occurred. Please try again.';
  }
}
